// What a voice says about itself, and what a silence leaves behind: the source
// notes, the ZTM gap band, the label a mute run wears, and the tag a voice is
// known by. Pure, like everything in `derive/`.
//
// Split out of `src/derive/axis.ts` (SPEC-viewfinder V3), which owns the axis
// itself — which rows exist, where the boundary is, and what the ring holds.
// These are the sentences the axis then places.
import type { Snapshot } from '../data/data.ts';
import type { SourceHealth } from '../data/feed.ts';
import { clockLabel } from '../shared/format.ts';
import type { AxisRow, Item, Voice } from '../shared/model.ts';
import { band } from './freshness.ts';

/**
 * How a voice names itself in a sentence — „Aquanet nie odpowiada.",
 * „Czekamy na odpowiedź poznan.pl." ZTM answers for both of its feeds: the
 * resident knows one ZTM, and `ZTM·K` is a tag on a row, not a company.
 */
const SOURCE: Record<Voice, { name: string; genitive: string; things: string }> = {
  ztm: { name: 'ZTM', genitive: 'ZTM', things: 'Pozycje pojazdów' },
  ztmk: { name: 'ZTM', genitive: 'ZTM', things: 'Komunikaty ZTM' },
  pl: { name: 'poznan.pl', genitive: 'poznan.pl', things: 'Wydarzenia' },
  aq: { name: 'Aquanet', genitive: 'Aquanetu', things: 'Zgłoszenia wody' },
};

/** The name a voice wears on a row, in a mute run and on its mark (§5.6). */
export const TAG: Record<Voice, string> = { ztm: 'ZTM', ztmk: 'ZTM·K', pl: 'PL', aq: 'AQ' };

/** `pojazd / pojazdy / pojazdów` — Polish counts in three (§7.2). */
export function plural(n: number, one: string, few: string, many: string): string {
  if (n === 1) return one;
  const last = n % 10;
  const teens = n % 100;
  return last >= 2 && last <= 4 && !(teens >= 12 && teens <= 14) ? few : many;
}

/** „ZTM · 16, 174 · 2 pojazdy · wyciszone" / „AQ, PL · 5 wpisów · poza solo" (§6). */
export function runLabel(items: Item[], voices: Voice[], reason: 'muted' | 'solo'): string {
  const tags = voices.map((v) => TAG[v]).join(', ');
  const tail = reason === 'muted' ? 'wyciszone' : 'poza solo';
  // A run that is nothing but vehicles counts vehicles and names their lines;
  // any other run counts entries, because „2 pojazdy" would be a lie about an
  // event or a water fault.
  if (voices.length === 1 && voices[0] === 'ztm') {
    const lines = [...new Set(items.flatMap((i) => i.lines ?? []))];
    const count = plural(items.length, 'pojazd', 'pojazdy', 'pojazdów');
    const head = lines.length ? `${tags} · ${lines.join(', ')}` : tags;
    return `${head} · ${items.length} ${count} · ${tail}`;
  }
  const count = plural(items.length, 'wpis', 'wpisy', 'wpisów');
  return `${tags} · ${items.length} ${count} · ${tail}`;
}

export type Note = {
  voice: Voice;
  text: string;
  tone: 'ink' | 'brick';
  connecting: boolean;
};

/**
 * What one voice has to say about itself, or nothing when it is simply working.
 * Every sentence is attributed to exactly one voice and is muted with it (§7.2,
 * D-11): the prototype signs one of them „AQ · ZTM·K" and mutes it under `aq`
 * alone, so silencing ZTM·K leaves its complaint on the screen. That is also
 * what keeps an Aquanet outage off the ZTM·K rows (#127) — no sentence here
 * ever speaks for a voice other than its own.
 *
 * The rows are on screen either way, so nothing is ever called failed: a source
 * that stopped answering has its rows dated and is named, a source that is
 * merely slow has its rows dated and is waited for (Decision 37, #83).
 */
export function sourceNote(voice: Voice, health: SourceHealth, now: number): Note | null {
  // ZTM's own state is the gap band's business (`ztmGap` below): a note beside
  // the band would say it twice.
  if (voice === 'ztm') return null;
  if (health.state === 'starting') return { voice, text: 'łączę się…', tone: 'ink', connecting: true };
  // `ok` with nothing fetched yet is one poll of skew, not a state to announce
  // (§9.3 row 16); `starting` above has already taken the case that is.
  if (health.state === 'ok' && health.updatedAt === 0) return null;

  const source = SOURCE[voice];
  const quiet = (text: string): Note => ({ voice, text, tone: 'ink', connecting: false });
  const fault = (text: string): Note => ({ voice, text, tone: 'brick', connecting: false });

  switch (band(health.updatedAt ? now - health.updatedAt : null, health.state !== 'down')) {
    case 'dead':
      // It never answered, so there is nothing to date and nothing to show.
      return fault(`${source.name} nie odpowiada. Nic jeszcze nie mamy.`);
    case 'stale':
      // It answered once and then stopped: the rows are still there, so the
      // note dates them and names the source that can no longer vouch for them.
      return fault(`${source.things} z ${clockLabel(health.updatedAt)}. ${source.name} nie odpowiada.`);
    case 'old':
      // Answering, just slow. Naming it „nie odpowiada" here would be the #127
      // lie moved to a new place, so this is a wait and reads like one.
      return quiet(`Czekamy na odpowiedź ${source.genitive}.`);
    case 'ageing':
    case 'live':
      break;
  }
  return null;
}

const quietNote = (voice: Voice, text: string): Note => ({ voice, text, tone: 'ink', connecting: false });

const NOTHING = { aq: 'Brak awarii wody.', ztmk: 'Brak komunikatów ZTM.' } as const;

/**
 * The sources that answered with nothing to report say so (§9.3 rows 28/36/41).
 * Each is dated by its own fetch time and never by ours: the old joint line
 * picked `max(aq, ztmk) || now`, so a fresh ZTM·K dated a stale Aquanet and a
 * source never fetched printed a check that never happened (#75). The owner's
 * ruling keeps the joint line where it is true — both fetched in the same
 * minute — and splits it into one note per voice where the hours differ; a
 * source never fetched keeps its own note with no clause at all.
 *
 * `voices` are the ones still audible with nothing on the rail; the joint line
 * only exists while both are, so muting either takes its half with it (D-11).
 */
export function nothingNotes(voices: readonly (keyof typeof NOTHING)[], health: Snapshot['health']): Note[] {
  const at = (voice: keyof typeof NOTHING) => health[voice].updatedAt;
  const [aq, ztmk] = [at('aq'), at('ztmk')];
  if (voices.length === 2 && aq && ztmk && clockLabel(aq) === clockLabel(ztmk)) {
    return [quietNote('aq', `Brak awarii wody i komunikatów ZTM. Sprawdzono ${clockLabel(aq)}.`)];
  }
  return voices.map((voice) =>
    quietNote(voice, at(voice) ? `${NOTHING[voice]} Sprawdzono ${clockLabel(at(voice))}.` : NOTHING[voice]),
  );
}

/**
 * The gap band ZTM leaves when it is not answering (§7.1, §7.2, D-2).
 *
 * `now` here is always the caller's real clock, never the hour the axis may
 * have been moved to: how old our ZTM snapshot is is a fact about this minute,
 * and measuring it against a borrowed 15:00 would report an hour of staleness
 * that never happened.
 */
export function ztmGap(snapshot: Snapshot, now: number, moved: boolean): AxisRow | null {
  const { health, ztmUpdatedAt, ztmFeedTs, serverReachable } = snapshot;
  // Two clocks, and they answer two different questions. How old the positions
  // are is ZTM's own stamp — our server refreshing a snapshot it could not
  // replace does not make the data any younger — and `ztmUpdatedAt` is only the
  // fallback for a wire that gave us no stamp to believe. *When it stopped* is
  // the health's clock: the last moment the source itself was answering.
  const age = ztmFeedTs ?? ztmUpdatedAt;
  const stopped = health.ztm.updatedAt;
  // One gap for the whole screen, not one complaint per voice (§9.3 row 20).
  if (!serverReachable) {
    return {
      kind: 'gap',
      at: now,
      label: 'brak połączenia z serwerem',
      sub: 'Spróbujemy ponownie za chwilę.',
    };
  }
  if (health.ztm.state === 'down') {
    return stopped
      ? {
          kind: 'gap',
          at: stopped,
          label: `Pozycje pojazdów z ${clockLabel(stopped)}`,
          sub: 'ZTM nie odpowiada. To ostatnie pozycje.',
        }
      : {
          kind: 'gap',
          at: now,
          label: 'ZTM nie odpowiada',
          sub: 'Nic jeszcze nie mamy. Sprawdzamy dalej.',
        };
  }
  // Answering, but the snapshot we are drawing has gone quiet (state 17). With
  // the centre moved in time there are no vehicle rows to date — the ZTM note
  // has already said why — so the band would be a complaint about nothing.
  if (!moved && health.ztm.state === 'ok' && age && band(now - age, true) !== 'live') {
    return {
      kind: 'gap',
      at: age,
      label: `dane z ${clockLabel(age)}`,
      sub: 'Czekamy na odpowiedź ZTM.',
    };
  }
  return null;
}
